// Table of Specifications ↔ item map: parsing, validation, DepEd banding and CSV
// import. Pure functions only (no DB, no DOM) so the rules are unit-testable.
//
// The one thing a TOS must carry to be usable is ITEM PLACEMENT — which item
// numbers measure each competency. A TOS that lists only a count per competency
// is still importable (`no_of_items`), but the items are then allocated
// sequentially in row order, which is only right if the paper was laid out that
// way; callers must say so.
//
// A competency is identified by its Learning Competency Description. The new
// curriculum has no competency codes and TESTIFY keys on the description, so
// short labels ("C1", "C2") are positional only — derived for display, never
// stored, and never used as a key.
import Papa from 'papaparse';
import type { CompetencyRecord } from '../db/schema.js';

// The passing mark and the DepEd intervention table now live in depedTiers.ts,
// shared with the item/quiz layer. Re-exported here so this module stays the one
// import site for everything TOS-related.
export {
  MIN_ITEMS_FOR_LEARNER,
  MASTERY_CUTOFF,
  interventionFor,
  type InterventionTier,
  type Intervention,
} from './depedTiers.js';
import { MIN_ITEMS_FOR_LEARNER } from './depedTiers.js';

/** Revised Bloom's Taxonomy levels, as DepEd TOS templates list them. */
export const COGNITIVE_LEVELS = [
  'Remembering',
  'Understanding',
  'Applying',
  'Analyzing',
  'Evaluating',
  'Creating',
] as const;

/** Compact positional label for a competency at index `i` ("C1", "C2", …). */
export const competencyLabel = (i: number): string => 'C' + (i + 1);

// ---- item spec ("1-3, 7, 14") -------------------------------------------

export interface ItemSpecResult {
  items: number[]; // sorted, unique
  error?: string;
}

/**
 * Parse an item-placement string into item numbers. Accepts comma/semicolon/space
 * separated singles and ranges: "1-3, 7, 14" → [1,2,3,7,14]. `maxItem` rejects
 * numbers the sheet doesn't have.
 */
export function parseItemSpec(spec: string, maxItem?: number): ItemSpecResult {
  const out = new Set<number>();
  const text = String(spec ?? '').trim();
  if (!text) return { items: [], error: 'No item numbers given' };
  for (const rawPart of text.split(/[,;]+|\s+(?=\d)/)) {
    const part = rawPart.trim();
    if (!part) continue;
    // en/em dashes are what a copy-pasted TOS actually contains
    const range = /^(\d+)\s*[-–—]\s*(\d+)$/.exec(part);
    if (range) {
      const from = Number(range[1]);
      const to = Number(range[2]);
      if (from < 1 || to < 1) return { items: [], error: `Item numbers start at 1 ("${part}")` };
      if (to < from) return { items: [], error: `Range runs backwards ("${part}")` };
      if (maxItem && to > maxItem) return { items: [], error: `Item ${to} is beyond this quiz's ${maxItem} items` };
      for (let q = from; q <= to; q++) out.add(q);
      continue;
    }
    if (!/^\d+$/.test(part)) return { items: [], error: `Not an item number or range ("${part}")` };
    const q = Number(part);
    if (q < 1) return { items: [], error: `Item numbers start at 1 ("${part}")` };
    if (maxItem && q > maxItem) return { items: [], error: `Item ${q} is beyond this quiz's ${maxItem} items` };
    out.add(q);
  }
  if (!out.size) return { items: [], error: 'No item numbers given' };
  return { items: [...out].sort((a, b) => a - b) };
}

/** Render item numbers back to a compact spec ("1-3, 7, 14") for display/edit. */
export function formatItemSpec(items: number[]): string {
  const sorted = [...new Set(items)].sort((a, b) => a - b);
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    parts.push(j - i >= 2 ? `${sorted[i]}-${sorted[j]}` : sorted.slice(i, j + 1).join(', '));
    i = j + 1;
  }
  return parts.join(', ');
}

/** First words of a competency description — for chips and tight table cells. */
export function shortDescription(description: string, max = 48): string {
  const text = description.trim();
  return text.length <= max ? text : text.slice(0, max - 1).replace(/\s+\S*$/, '') + '…';
}

// ---- map-level validation ------------------------------------------------

export interface MapCoverage {
  totalItems: number;
  mappedItems: number[];
  unmappedItems: number[];
  /** items claimed by more than one competency — never produced by the editor or
   *  the importer (both reject collisions), but a hand-edited map could have them. */
  duplicateItems: number[];
  /** positional labels of competencies with fewer than MIN_ITEMS_FOR_LEARNER items. */
  thinCompetencies: string[];
  complete: boolean;
}

/** Coverage of a quiz's items by a competency list — drives the editor's summary. */
export function mapCoverage(competencies: CompetencyRecord[], totalItems: number): MapCoverage {
  const seen = new Map<number, number>();
  for (const c of competencies) for (const q of c.items) seen.set(q, (seen.get(q) ?? 0) + 1);
  const mappedItems = [...seen.keys()].filter((q) => q >= 1 && q <= totalItems).sort((a, b) => a - b);
  const unmappedItems: number[] = [];
  for (let q = 1; q <= totalItems; q++) if (!seen.has(q)) unmappedItems.push(q);
  return {
    totalItems,
    mappedItems,
    unmappedItems,
    duplicateItems: [...seen.entries()].filter(([, n]) => n > 1).map(([q]) => q).sort((a, b) => a - b),
    thinCompetencies: competencies
      .map((c, i) => (c.items.length < MIN_ITEMS_FOR_LEARNER ? competencyLabel(i) : ''))
      .filter(Boolean),
    complete: unmappedItems.length === 0,
  };
}

/**
 * Check one competency before it goes into the map: a description, items in range,
 * and no item already claimed by another competency. `exceptIndex` is the row being
 * edited (its own description and items don't count as collisions).
 */
export function validateCompetency(
  candidate: { description: string; items: number[] },
  all: CompetencyRecord[],
  totalItems: number,
  exceptIndex?: number,
): string | null {
  const description = candidate.description.trim();
  if (!description) return 'Learning competency description is required';
  const rest = all.filter((_, i) => i !== exceptIndex);
  if (rest.some((c) => c.description.trim().toLowerCase() === description.toLowerCase())) {
    return 'That learning competency is already in this map';
  }
  if (!candidate.items.length) return 'At least one item number is required';
  const bad = candidate.items.find((q) => q < 1 || q > totalItems);
  if (bad !== undefined) return `Item ${bad} is beyond this quiz's ${totalItems} items`;
  // One competency per item: overlapping rows would double-count a learner's
  // wrong answer into two remediation lists.
  const taken = new Map<number, string>();
  rest.forEach((c) => {
    const label = competencyLabel(all.indexOf(c));
    for (const q of c.items) taken.set(q, label);
  });
  const clash = candidate.items.find((q) => taken.has(q));
  if (clash !== undefined) return `Item ${clash} is already mapped to ${taken.get(clash)}`;
  return null;
}

// ---- TOS CSV import -----------------------------------------------------

export const TOS_TEMPLATE_CSV =
  'learning_competency,items,no_of_items,cognitive_level\n' +
  '"Uses context clues (synonyms, antonyms, word parts) to find meaning of unfamiliar words","1-3, 14",,Understanding\n' +
  '"Infers the thoughts and feelings of characters in a narrative","4-6, 15, 22",,Analyzing\n' +
  '"Identifies figures of speech used in a text",,4,Remembering\n';

export interface TosRow extends CompetencyRecord {
  /** true when items came from `no_of_items` rather than explicit placement. */
  allocated: boolean;
}
export interface TosParseResult {
  rows: TosRow[];
  errors: Array<{ line: number; message: string }>;
  headerError?: string;
  /** true if any row's items were allocated sequentially from a count. */
  anyAllocated: boolean;
}

/** Header aliases, so a TOS exported from Excel usually imports as-is. Legacy
 *  competency-code columns are accepted as a description of last resort — old
 *  TOS files still have them, even though the curriculum no longer uses codes. */
const ALIAS: Record<string, string[]> = {
  description: [
    'learning_competency',
    'competency',
    'description',
    'competency_description',
    'learning_competency_description',
    'melc',
  ],
  legacyCode: ['competency_code', 'code', 'melc_code'],
  items: ['items', 'item_placement', 'item_numbers', 'item_no', 'placement'],
  count: ['no_of_items', 'n_items', 'num_items', 'number_of_items', 'item_count'],
  level: ['cognitive_level', 'rbt', 'rbt_level', 'level', 'cognitive_domain'],
};

function pick(row: Record<string, string>, keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/**
 * Parse a TOS CSV into competency rows. Partial success by design (import the
 * good rows, report the bad ones), matching the student importer. Rows with
 * explicit item placement are honoured; rows with only a count are allocated
 * sequentially from the lowest item not yet claimed.
 */
export function parseTosCsv(text: string, totalItems: number): TosParseResult {
  const out: TosParseResult = { rows: [], errors: [], anyAllocated: false };
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    // Normalise real-world TOS headers: "No. of Items" → no_of_items,
    // "Learning Competency" → learning_competency.
    transformHeader: (h) => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
  });
  const fields = parsed.meta.fields ?? [];
  const has = (keys: string[]) => keys.some((k) => fields.includes(k));
  if (!has(ALIAS.description) && !has(ALIAS.legacyCode)) {
    out.headerError =
      'CSV needs a header row with a learning competency column (see the template).';
    return out;
  }
  if (!has(ALIAS.items) && !has(ALIAS.count)) {
    out.headerError = 'CSV needs either an "items" column (item placement, e.g. "1-3, 7") or a "no_of_items" column.';
    return out;
  }

  const taken = new Map<number, string>();
  const usedDescriptions = new Set<string>();
  // Sequential allocation cursor for count-only rows.
  let cursor = 1;
  const nextFree = () => {
    while (cursor <= totalItems && taken.has(cursor)) cursor++;
    return cursor;
  };

  parsed.data.forEach((raw, i) => {
    const line = i + 2; // header is line 1
    // The competency IS the description; a legacy code column only stands in when
    // the file has no description at all.
    const description = pick(raw, ALIAS.description) || pick(raw, ALIAS.legacyCode);
    const itemsSpec = pick(raw, ALIAS.items);
    const countSpec = pick(raw, ALIAS.count);
    const level = pick(raw, ALIAS.level);
    if (!description && !itemsSpec && !countSpec) return; // blank line
    if (!description) return void out.errors.push({ line, message: 'Learning competency is required' });
    if (usedDescriptions.has(description.toLowerCase())) {
      return void out.errors.push({ line, message: `Duplicate learning competency ("${shortDescription(description)}")` });
    }

    let items: number[];
    let allocated = false;
    if (itemsSpec) {
      const parsedSpec = parseItemSpec(itemsSpec, totalItems);
      if (parsedSpec.error) return void out.errors.push({ line, message: parsedSpec.error });
      items = parsedSpec.items;
      const clash = items.find((q) => taken.has(q));
      if (clash !== undefined) {
        return void out.errors.push({ line, message: `Item ${clash} is already mapped to ${taken.get(clash)}` });
      }
    } else {
      if (!/^\d+$/.test(countSpec)) {
        return void out.errors.push({ line, message: `Needs item placement or a whole number of items ("${countSpec || 'blank'}")` });
      }
      const count = Number(countSpec);
      if (count < 1) return void out.errors.push({ line, message: 'Number of items must be at least 1' });
      items = [];
      for (let k = 0; k < count; k++) {
        const q = nextFree();
        if (q > totalItems) {
          return void out.errors.push({
            line,
            message: `Not enough items left — the TOS allocates more than this quiz's ${totalItems} items`,
          });
        }
        items.push(q);
        taken.set(q, competencyLabel(out.rows.length));
      }
      allocated = true;
      out.anyAllocated = true;
    }

    const label = competencyLabel(out.rows.length);
    for (const q of items) taken.set(q, label);
    usedDescriptions.add(description.toLowerCase());
    out.rows.push({ description, items, cognitiveLevel: level || undefined, allocated });
  });

  return out;
}

/** Strip the importer's bookkeeping so rows can be persisted as-is. */
export function toCompetencyRecords(rows: TosRow[]): CompetencyRecord[] {
  return rows.map(({ description, items, cognitiveLevel }) => ({ description, items, cognitiveLevel }));
}
